import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartHandshake, Stethoscope, Baby, ArrowRight } from "lucide-react";

const services = [
  {
    icon: HeartHandshake,
    name: "Patients & Elderly Care",
    description: "Compassionate daily assistance for elderly loved ones — from basic caregiving to personal care with dignity.",
    price: "Starting ₹1,000/day",
    href: "/services/patients-elderly-care",
    color: "bg-[#14B8A6]/10",
    iconColor: "text-[#14B8A6]",
  },
  {
    icon: Stethoscope,
    name: "Home Nursing",
    description: "Professional nursing care including nurse visits, home nursing, and critical care nursing at your doorstep.",
    price: "Starting ₹650/visit",
    href: "/services/home-nursing",
    color: "bg-blue-50",
    iconColor: "text-blue-500",
  },
  {
    icon: Baby,
    name: "Nanny / Baby Sitting",
    description: "Safe, nurturing childcare by experienced and verified nannies. Full-time, part-time, and live-in options.",
    price: "Starting ₹800/day",
    href: "/services/nanny-baby-sitting",
    color: "bg-rose-50",
    iconColor: "text-rose-400",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function ServicesOverview() {
  return (
    <section id="services" data-testid="services-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-16">
          <span className="text-sm font-semibold text-[#14B8A6] uppercase tracking-wider font-body">
            Our Services
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] tracking-tight mt-3">
            Professional Healthcare at Your Doorstep
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#64748B] mt-4 max-w-2xl font-body">
            From elder care to home nursing, we provide comprehensive services tailored to your family's needs.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {services.map((service) => (
            <motion.div key={service.name} variants={item}>
              <Link
                to={service.href}
                data-testid={`service-card-${service.href.split("/").pop()}`}
                className="group block relative overflow-hidden rounded-3xl bg-white p-8 border border-slate-100 hover:border-[#14B8A6]/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/5 h-full"
              >
                <div className={`service-icon w-14 h-14 rounded-2xl ${service.color} flex items-center justify-center mb-6`}>
                  <service.icon className={`w-7 h-7 ${service.iconColor}`} strokeWidth={1.5} />
                </div>

                <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-3">
                  {service.name}
                </h3>
                <p className="text-sm leading-relaxed text-[#64748B] font-body mb-6">
                  {service.description}
                </p>

                <div className="flex items-center justify-between mt-auto">
                  <p className="font-heading text-lg font-bold text-[#14B8A6]">
                    {service.price}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#dd6b8e] group-hover:gap-2 transition-all font-body">
                    View Details <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
